/*
 * Purpose: Inline issue alert — message + required contextual fix action
 * Last modified: 2026-07-01 | Agent
 * Completeness: 100
 */
import { Link } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { resolveActionTierProps, type ActionTier } from '@/lib/actionHierarchy';
import { cn } from '@/lib/utils';

export type RemediationAction = {
  label: string;
  onClick?: () => void;
  href?: string;
};

interface ContextualErrorProps {
  message: string;
  action?: RemediationAction;
  secondaryAction?: RemediationAction;
  ghostAction?: RemediationAction;
  className?: string;
}

function ActionButton({ action, tier }: { action: RemediationAction; tier: ActionTier }) {
  const tierProps = resolveActionTierProps(tier);
  if (action.href) {
    return (
      <Button
        size="sm"
        variant={tierProps.variant}
        className={cn('shrink-0', tierProps.className)}
        asChild
      >
        <Link to={action.href}>{action.label}</Link>
      </Button>
    );
  }
  return (
    <Button
      size="sm"
      variant={tierProps.variant}
      className={cn('shrink-0', tierProps.className)}
      onClick={action.onClick}
    >
      {action.label}
    </Button>
  );
}

export function ContextualError({
  message,
  action,
  secondaryAction,
  ghostAction,
  className,
}: ContextualErrorProps) {
  return (
    <div
      role="alert"
      className={cn(
        'ab-interactive-alert rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2.5 text-sm flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3',
        className
      )}
    >
      <div className="flex items-start gap-2 min-w-0 flex-1">
        <AlertCircle className="h-4 w-4 text-destructive shrink-0 mt-0.5" aria-hidden />
        <p className="text-destructive text-xs sm:text-sm">{message}</p>
      </div>
      {(action || secondaryAction || ghostAction) && (
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          {action && <ActionButton action={action} tier="primary" />}
          {secondaryAction && <ActionButton action={secondaryAction} tier="secondary" />}
          {ghostAction && <ActionButton action={ghostAction} tier="ghost" />}
        </div>
      )}
    </div>
  );
}
